import { Injectable } from '@angular/core';
import { MidiaImageBase64Dto } from '../../_infrastructure/dtos/core/midia/MidiaImagemBase64Dto';

@Injectable({
  providedIn: 'root'
})
export class MediaImageService {
  private readonly tiposPadrao: Record<string, string[]> = {
    JPEG: ['image/jpeg', 'image/jpg'],
    PNG: ['image/png'],
    WebP: ['image/webp']
  };

  private readonly nomesPorTipoMime: Record<string, string> = {
    'image/jpeg': 'uma imagem JPEG',
    'image/jpg': 'uma imagem JPG',
    'image/png': 'uma imagem PNG',
    'image/webp': 'uma imagem WebP',
    'application/pdf': 'um PDF',
    'application/msword': 'um documento do Word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'um documento do Word',
    'application/vnd.ms-excel': 'uma planilha do Excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'uma planilha do Excel',
    'application/vnd.ms-powerpoint': 'uma apresentação do PowerPoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'uma apresentação do PowerPoint',
    'text/plain': 'um arquivo TXT',
    'text/csv': 'um arquivo CSV'
  };

  private readonly nomesFormatos: Record<string, string> = {
    JPEG: 'JPEG',
    PNG: 'PNG',
    WebP: 'WebP'
  };

  async processarImagem(
    file: File,
    opcoes?: {
      tamanhoMaxMb?: number;
      tiposPermitidos?: string[];
      minWidth?: number;
      minHeight?: number;
      maxWidth?: number;
      maxHeight?: number;
    }
  ): Promise<MidiaImageBase64Dto> {
    this.validarTamanhoETipo(
      file,
      opcoes?.tamanhoMaxMb ?? 2,
      opcoes?.tiposPermitidos ?? this.obterTiposPermitidos()
    );

    const base64Completo = await this.lerComoBase64(file);
    const midia = await this.criarMidiaDto(file, base64Completo);

    if (opcoes?.minWidth && opcoes?.minHeight) {
      if (midia.largura < opcoes.minWidth || midia.altura < opcoes.minHeight) {
        throw new Error(`A imagem deve ter no mínimo ${opcoes.minWidth}x${opcoes.minHeight}px.`);
      }
    }

    if (opcoes?.maxWidth && opcoes?.maxHeight) {
      if (midia.largura > opcoes.maxWidth || midia.altura > opcoes.maxHeight) {
        throw new Error(`A imagem deve ter no máximo ${opcoes.maxWidth}x${opcoes.maxHeight}px.`);
      }
    }

    return midia;
  }

  gerarPreview(midia: MidiaImageBase64Dto): string {
    return `data:image/*;base64,${midia.base64}`;
  }

  private async lerComoBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private async criarMidiaDto(file: File, base64Completo: string): Promise<MidiaImageBase64Dto> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          base64: base64Completo.split(',')[1],
          nome: file.name,
          altura: img.height,
          largura: img.width,
          tamanhoKb: (file.size / 1024).toFixed(2)
        });
      };
      img.onerror = reject;
      img.src = base64Completo;
    });
  }

  private validarTamanhoETipo(
    file: File,
    tamanhoMaxMb: number,
    tiposPermitidos: string[]
  ): void {
    const tamanhoEmMb = file.size / (1024 * 1024);
    if (tamanhoEmMb > tamanhoMaxMb) {
      throw new Error(`O arquivo excede o tamanho máximo permitido de ${tamanhoMaxMb} MB.`);
    }

    if (!tiposPermitidos.includes(file.type)) {
      throw new Error(
        `Esse arquivo parece ser ${this.obterTipoArquivo(file)}. ` +
        `Para este campo, envie uma imagem nos formatos: ${this.obterNomesTiposPermitidos(tiposPermitidos)}.`
      );
    }
  }

  private obterTiposPermitidos(): string[] {
    return Object.values(this.tiposPadrao).flat();
  }

  private obterTipoArquivo(file: File): string {
    return this.nomesPorTipoMime[file.type] ?? `um arquivo do tipo ${file.type || this.obterExtensao(file.name)}`;
  }

  private obterNomesTiposPermitidos(tiposPermitidos: string[]): string {
    const nomes = Object.entries(this.tiposPadrao)
      .filter(([, tipos]) => tipos.some((tipo) => tiposPermitidos.includes(tipo)))
      .map(([nome]) => this.nomesFormatos[nome] ?? nome);

    return [...new Set(nomes)].join(', ');
  }

  private obterExtensao(nome: string): string {
    const partes = nome.split('.');
    return partes.length > 1 ? partes.pop()!.toLowerCase() : '';
  }
}
